<template>
	<q-dialog
		v-if="dialog"
		v-model="dialog"
		:persistent="props.persistent"
		:transition-show="props.transitionShow"
		:transition-hide="props.transitionHide"
		:transition-duration="props.transitionDuration"
		@hide="onClose"
	>
		<div class="base-modal" ref="modalEl" :style="computedStyles">
			<!-- <q-bar class="base-modal__bar">
				<div class="base-modal__bar-title">{{ props.title }}</div>
				<q-space />
				<app-icon
					class="base-modal__close-btn"
					name="close"
					width="20px"
					color="#979797"
					hover-color="#499259"
					cursor="pointer"
					title=""
					v-close-popup
					@click="onClose"
				/>
			</q-bar> -->
			<div class="base-modal__body">
				<app-loader
					v-if="loading"
					:style="loaderStyle"
				/>
				<slot />
			</div>
		</div>
	</q-dialog>
</template>

<script setup lang="ts">
import {
	computed,
	ref
} from "vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

export type QuasarTransitions =
	"slide-right"
	| "slide-left"
	| "slide-up"
	| "slide-down"
	| "fade"
	| "scale"
	| "rotate"
	| "flip-right"
	| "flip-left"
	| "flip-up"
	| "flip-down"
	| "jump-right"
	| "jump-left"
	| "jump-up"
	| "jump-down"

type ModalProps = {
	loading?: boolean,
	persistent?: boolean,
	transitionShow?: QuasarTransitions,
	transitionHide?: QuasarTransitions,
	transitionDuration?: number,
	width?: number | string,
	height?: number | string,
	title?: string,
	position?: { x: number; y: number } | null
}

const props = withDefaults(defineProps<ModalProps>(), {
	loading: false,
	persistent: true,
	transitionShow: "scale",
	transitionHide: "scale",
	transitionDuration: 0,
	width: "55vw",
	height: "95vh",
	title: "",
	position: null
})

const emit = defineEmits<{
	(e: 'on-decline'): void
	(e: 'on-accept'): void
	(e: 'on-close'): void
}>()

const dialog = ref(false)

const computedStyles = computed(() => {
  const base = {
    maxHeight: '95vh',
    maxWidth: '55vw',
    width: typeof props.width === 'number'
      ? `${props.width}px`
      : props.width,
    height: typeof props.height === 'number'
      ? `${props.height}px`
      : props.height,
  } as Record<string, string>

  if (props.position) {
    base.position = 'fixed'
    base.left = `${props.position.x}px`
    base.top = `${props.position.y}px`
    base.margin = '0'
  }

  return base
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const modalEl = ref<HTMLElement | null>(null)

const onOpen = () => {
	dialog.value = true
}

const onClose = () => {
	emit("on-close")
	dialog.value = false
}

const onDecline = () => {
	emit("on-decline")
	dialog.value = false
}

const onAccept = () => {
	emit("on-accept")
	dialog.value = false
}

defineExpose({
  onOpen,
  onClose,
  onAccept,
  onDecline,
  modalEl
})
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";

.base-modal {
	display: flex;
	flex-direction: column;
	width: 100%;
	background: #fff;
	border-radius: 16px;
	padding: 4px;

	&__bar {
		border-bottom: 1px solid #D2D2D2;
		padding: 12px 12px 15px 12px;
		height: fit-content;
		color: #333333;
		background-color: transparent;
		box-sizing: border-box;

		&-title {
			font-size: 14px;
			line-height: 18px;
			font-weight: 600;
			color: $color-text_main;
		}
	}

	&__body {
		position: relative;
		flex: 1;
		width: 100%;
		overflow-x: auto;
	}
}
</style>
